import {PolymerElement} from "@polymer/polymer/polymer-element.js";
import template from "./ns-personal-offer-landing.html";
import {
  util,
  ShoppingContext,
  PersonalOfferStorageService,
  SponsorStorageService,
  RunConfigService,
  StoreFrontSponsorStorageService,
  events
} from "@nuskin/ns-util";
import {CartService, ProductStatusService, CurrencyService, PersonalOfferService} from "@nuskin/ns-shop";
import {AccountService, AuthenticationService, UserService} from "@nuskin/ns-account";

/**
 * <p>ns-personal-offer-landing is the component used for displaying and adding personal offer products
 * to the cart.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/share/UNPDOP5DE92#/screens/334237877" target="_blank">
 * InVision Design Documents
 * </a>
 *
 * <h2>Demo</h2>
 *
 * <demo-snippet class="demoSnippet">
 *  <template>
 *    <ns-personal-offer-landing></ns-personal-offer-landing>
 *  </template>
 * </demo-snippet>
 */
export class NsPersonalOfferLanding extends PolymerElement {
  constructor() {
    super();
  }

  static get is() {
    return "ns-personal-offer-landing";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Products array passed to component included in offer.
       * ns-product-small is then used to display product data.
       * These are only default products for demonstration.
       */
      items: {
        type: Array
      },
      /**
       * These are translations the Personal Offer landing page dialog in AEM
       * utilizes.
       */
      translations: {
        type: Object,
        value: {
          welcome: 'Welcome!',
          bundleTotal: 'Bundle Total - {price}',
          discountPercentagePrice: '{percent}% Off - {savings} Savings',
          addAllItems: 'Add All Items ({qty}) To Bag',
          offerNotFound: 'Offer Not Found. Please contact {user}.',
          offerDiscountMessage: 'Your {discount}% discount will be available on checkout.',
          offerExpired: 'Offer Expired. Please contact {user}.',
          offerName: '{name}'
        }
      },
      hideDiscount: {
        type: Boolean,
        value: true
      },
      productSelectionNeeded: {
        type: Boolean,
        value: false
      },
      productSelectionNeededTypes: {
        type: Array,
        value: []
      },
      offerNotFound: {
        type: Boolean,
        value: false
      },
      offerLoading: {
        type: Boolean,
        value: true
      },
      hideBottomActions: {
        type: Boolean,
        value: false
      },
      baseSkusNotSelected: {
        type: Array,
        value: []
      },
      offerExpired: {
        type: Boolean,
        value: false
      }
    };
  }

  async ready() {
    super.ready();
    this.country = RunConfigService.getRunConfig().country;
    this.language = RunConfigService.getRunConfig().language;
    this.currencyCode = CurrencyService.getCurrency(this.country).currencyCode;

    events.subscribe(events.authentication.REQUEST_FOR_LOGIN, function() {
      CartService.clearCart();
    });

    events.subscribe(events.shop.PRODUCT_STATUS_RESULT, async data => {
      await this._productsChanged();
    });

    events.subscribe(events.shop.VARIANT_CHANGED, (sku) => {
      let card = this.shadowRoot.querySelector('#offerProduct' + sku);
      let variantDropdown = card.shadowRoot.querySelector('#variantDropdown');
      let label = variantDropdown.shadowRoot.querySelector('.form-label');
      label.removeAttribute('style');
    });

    // COM-2736: Add new GA event for Personal Offer Click Impressions. Event from ns-product-small
    window.addEventListener('nsProductSmallTap', (e) => {
      if (e.detail.path !== undefined && e.detail.path[1].id === 'cardContent') {
        let model = e.detail.model.__data;
        events.publish(events.shop.PRODUCT_IMPRESSION_CLICK, {
          'ecommerce': {
            'click': {
              'actionField': {},           // Optional list property.
              'products': [{
                'name': model.item.name,      // Name or ID is required.
                'id': model.item.SKU,         // This is the SKU
                'price': model.item.price,
                // 'category': 'Nu Skin',// this is the EDW category for the product
                'position': model.index
              }]
            }
          }
        });
      }
    });

    const userId = this._getParameterByName('userId');
    const pitchId = this._getParameterByName('pitchId');
    await this._getOfferData(userId, pitchId);

    // v2
    let v2Offer = await PersonalOfferService.getOfferV2(userId, pitchId)

    // SCL-1071
    if(v2Offer && v2Offer.expirationDate) {
      let currentDate = new Date();
      currentDate.setHours(0);
      currentDate.setMinutes(0);
      currentDate.setSeconds(0);
      currentDate.setMilliseconds(0);

      // The expiration = 00:00 the next day (CX9-8164)
      let expirationDate = new Date(v2Offer.expirationDate);
      expirationDate.setTime(expirationDate.getTime() + expirationDate.getTimezoneOffset() * 60000);
      expirationDate.setHours(0);
      expirationDate.setMinutes(0);
      expirationDate.setSeconds(0);
      expirationDate.setMilliseconds(0);

      if (expirationDate.getTime() < currentDate.getTime()) {
        this.set('offerExpired', true);
        const displayName = await StoreFrontSponsorStorageService.getDisplayName({
          getAccountInfo: AccountService.getAccountInfo,
          userId
        });
        if (displayName) {
          this.set('translations.offerExpired', this.translations.offerExpired.replace('{user}', displayName));
        }
        return
      }
    }

    this.useADR = v2Offer && v2Offer.useADR || null
    let sessionData = localStorage.getItem('personalOffer_v2')
      ? JSON.parse(localStorage.getItem('personalOffer_v2'))
      : null
    let currentSession = sessionData ? sessionData.sessionId : null

    // if new session
    if(!currentSession || !v2Offer || !v2Offer.sessions || !v2Offer.sessions[currentSession]) {
      try {
        let res = await PersonalOfferService.callViewed(userId, pitchId)
        PersonalOfferStorageService.setSessionId(res.sessionId)
      } catch(err) {
        console.error('Failed updating product status to \'viewed\'')
      }
    }
  }

  /**
   * This method checks the product status of each product and then adds each
   * of the products to the cart
   */
  async addFullOfferToCart() {
    let productSmalls = this.shadowRoot.querySelectorAll("ns-product-small");
    let productSkus = [];
    this.baseSkusNotSelected = [];
    this.productSelectionNeededTypes = [];
    let preventAddAllAction = false;
    productSmalls.forEach((product) => {
      if (product.__data.baseSku) {
        if (product.__data.selectedVariantSku) {
          productSkus.push(product.__data.selectedVariantSku);
        } else {
          preventAddAllAction = true;
          this.baseSkusNotSelected.push(product);
          this.productSelectionNeededTypes.push(product.__data.product.variantDropdownPlaceholder);
        }
      } else {
        productSkus.push(product.displaySku);
      }
    });

    if (preventAddAllAction) {
      this.baseSkusNotSelected.forEach((product) => {
        let productSmall = product.id;
        let card = this.shadowRoot.querySelector('#' + productSmall);
        let variantDropdown = card.shadowRoot.querySelector('#variantDropdown');
        let label = variantDropdown.shadowRoot.querySelector('.form-label');
        label.style.color = 'var(--redshirt)';
        label.style.fontSize = '16px';
        label.style.lineHeight = '20px';
        label.style.fontWeight = 'var(--font-weight-semibold)';
        let cardContainer = card.shadowRoot.querySelector('.card-container');
        let cardAction = card.shadowRoot.querySelector('#cardAction');
        cardAction.opened = true;
        if (!util.MobileUtils.isMobile()) {
          let cardActionWrap = cardContainer.querySelector('.card-action-wrap');
          cardContainer.style.boxShadow = '0 5px 10px 0 rgba(194, 57, 52)';
          cardActionWrap.style.boxShadow = '0 8px 8px 0 rgba(194, 57, 52)';
        }

        let offset = cardContainer.offsetTop;
        // Using jQuery because Safari doesn't support traditional smooth scrolling
        $('html, body').animate({
          scrollTop: offset
        }, 500);

        this.productSelectionNeeded = true;
      });
    } else {
      let that = this;
      let userId = null;
      if (UserService.getUser()) {
        userId = UserService.getUser().id;
      }

      let statusData = await ProductStatusService.getProductStatus(productSkus, this.country)
      let referrer = "unknown";
      if (typeof (statusData.referrer) === 'string' && statusData.referrer.length > 1) {
        referrer = statusData.referrer;
      }
      let itemsArray = [];
      if (statusData) {
        for (let i = 0; i < productSmalls.length; i++) {
          for (let j = 0; j < statusData.length; j++) {

            if (productSkus[i] == statusData[j].SKU) {
              let product = statusData[j];

              let options = {
                sku: product.SKU,
                cntryCd: that.country,
                language: that.language,
                qty: productSmalls[i].qty,
                userId: userId,
                isAdr: !!this.useADR,
                isAtpCheck: false,
                referrer: "myStore"
              };

              await CartService.addSkuToCart(options)
              events.publish(events.shop.ADD_TO_CART, options)

              itemsArray.push({
                'name': productSmalls[i].__data.displayTitle,
                'id': options.sku,
                'price': productSmalls[i].price,
                'quantity': options.qty
              })

              events.publish(events.shop.ADD_TO_CART_NEW,{
                'event': 'addToCart',
                'ecommerce': {
                  'currencyCode': this.currencyCode,
                  'add': {                      // 'add' actionFieldObject measures.
                    'actionField': {
                      'list': referrer
                    },
                    'products': [{              // adding a product to a shopping cart.
                      'name': productSmalls[i].__data.displayTitle,
                      'id': options.sku,
                      'price': productSmalls[i].price,
                      'quantity': options.qty,
                      'method': 'personaloffer',    // personal offer or quickView or productPage or cart
                      'cartType': this.useADR ? 'ADR' : 'Standard'         // ADR or Standard
                    }]
                  }
                }
              });
            }
          }
        }

        events.publish(events.shop.CART_CHECKOUT, {
          'event': 'checkout',
          'ecommerce': {
            'currencyCode': this.currencyCode,
            'checkout': {
              'actionField': {'step': 2},
              'products': itemsArray
            }
          }
        });
        try {
          await PersonalOfferService.callProduct(this._getParameterByName("userId"), this._getParameterByName("pitchId"))
        } catch(err) {
          console.error('Failed updating product status to \'product_added\'')
        }
        CartService.goToCartPage({isMySite: true});
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    this._updatePriceCallback = async (evt) => await this._updateOfferPrice();
    this.addEventListener("price-updated", this._updatePriceCallback);
    // SCL-856 TODO: manage signin visibility in config
    try {
      if(window.innerWidth < 1024) {
        let header = document.querySelector('ns-header').shadowRoot
        header.querySelector('.utility-bar-area').style.display="block"
        header.querySelector('.utility-bar-area').style.padding="0 1%"
        header.querySelector('.right-col').style.display="block"
      }
    } catch (err){}

  }

  disconnectedCallback() {
    this.removeEventListener("price-updated", this._updatePriceCallback);
  }

  /**
   * Set the PDP url to contain the "mysite" selector for Personal Offer
   */
  getPdpUrl(sku) {
    return util.getLocaleUrl() + "products/product." + sku + ".mysite.html";
  }

  setMemberPricing(flag) {
    let showWholeSalePricing = !!flag
    let contextOptions = {
      showWholeSalePricing
    }

    if(showWholeSalePricing) {
      contextOptions.allowAnonymousShopping = false
      contextOptions.preferredSignupOnly = true
    } else {
      contextOptions.allowAnonymousShopping = true
      contextOptions.preferredSignupOnly = false
    }
    // personal offer and mysite contexts are combined
    ShoppingContext.setShoppingContext(ShoppingContext.PERSONAL_OFFER, contextOptions)
  }

  /**
   * Gets data from Firebase and does several things:
   * 1. Sets the Offer object as "this.offer"
   * 2. Sets the Offer ID in the offer object
   * 3. Sets the landingPageURL in the offer object to be used on Order Confirmation
   * 4. Sets the Sponsor ID in Session Storage as "sponsor"
   * 5. Sets the Offer details in Session Storage as "personalOffer"
   * 6. Sets the Discount object for the offer as "this.discount"
   * 7. Sets the Items array of objects as "this.items"
   * @param {String} userId - grabbed from parameter in URL
   * @param {String} offerId - grabbed from parameter in URL
   */
  async _getOfferData(userId, offerId) {
    let localOffer = PersonalOfferStorageService.getPersonalOffer();
    if (localOffer && localOffer.pitchID !== offerId) {
      CartService.clearCart();
    }

    let response = await PersonalOfferService.getPitch(userId, offerId)

    let responseObject = response && response.data && Object.keys(response.data).length ? response.data : null

    if(!responseObject || (responseObject && !responseObject.items && !responseObject.products)) {
      response = {
        data: await PersonalOfferService.getOfferV2(userId, offerId)
      }
    }

    responseObject = response && response.data && Object.keys(response.data).length ? response.data : null

    // mapping new data to match old schema
    if (responseObject && responseObject.active !== false && !responseObject.isDeleted) {
      responseObject.pitchID = offerId;
      responseObject.sapId = userId;

      responseObject.congratulations = responseObject.congratulations || responseObject.confirmationMessage;
      responseObject.isGroupOffer = responseObject.isGroupOffer || responseObject.isGroup;
      responseObject.language = responseObject.language || responseObject.languageCode;
      responseObject.greeting = responseObject.greeting || responseObject.message;
      responseObject.items = responseObject.items || responseObject.products.map(item => {
        return {
          SKU: item.sku,
          RequestedQuantity: item.qty
        }
      })

      // SCL-1425 Member Pricing
      this.setMemberPricing(responseObject.useMemberPricing)

      responseObject.landingPageURL = window.location.href;
      SponsorStorageService.setSponsor(responseObject.sapId);
      if (StoreFrontSponsorStorageService.getStoreFrontSponsor()) {
        this.sponsorName = await StoreFrontSponsorStorageService.getDisplayName({
          getAccountInfo: AccountService.getAccountInfo,
          userId: responseObject.sapId
        }) || '';
      } else {
        await this._getSponsorName(responseObject.sapId)
      }

      let offerName = this.translations.offerName.replace('{name}', responseObject.name);
      responseObject.name = offerName;
      this.offer = responseObject;

      let user = UserService.getUser();
      if (AuthenticationService.isLoggedIn() && user) {
        if (user.accountType == "10" || user.accountType == "30") {
          this.discount = {
            code: 'RETAILPRICE',
            description: 'RetailPricing',
            multiplier: 1
          }
          responseObject.discount = this.discount;
        } else {
          this.discount = responseObject.discount;
        }
      } else {
        this.discount = responseObject.discount;
      }

      PersonalOfferStorageService.setPersonalOffer(responseObject);
      let multiplier = this.discount ? this.discount.multiplier : 1
      if (Number(multiplier) == 1) {
        this.hideDiscount = true;
      } else {
        this.hideDiscount = false;
      }

      this.items = responseObject.items

      events.publish(events.shop.PRODUCT_IMPRESSION, {
        "ecommerce": {
          "currencyCode": this.currencyCode,
          "impressions": this.items.map((item, index) => {
            return {
              "id": item.SKU,
              "name": item.name,
              "price": item.price,
              "quantity": item.RequestedQuantity,
              // "category": item.category, // category comes from EDW categories
              "position": index,
              "list": "pitchapp"// homepage ad OR catalog OR you might also like OR pitchapp or category page
            };
          })
        }
      });
    } else {
      this.offerNotFound = true;
      await this._getSponsorName(userId);
      this.offerLoading = false;
    }
  }

  /**
   * Get Sponsor data and set storefront sponsor in session storage
   * @param {String} userId
   */
  async _getSponsorName(userId) {
    if (!this.sponsorName && !this.displayNameProm) {
      this.displayNameProm = StoreFrontSponsorStorageService.getDisplayName({
        getAccountInfo: AccountService.getAccountInfo,
        userId
      });
      const displayName = await this.displayNameProm;
      this.sponsorName = displayName || '';
    }

    let notFoundMessage = this.translations.offerNotFound;
    let notFoundMessageReplaced = notFoundMessage.replace('{user}', this.sponsorName);
    this.set('translations.offerNotFound', notFoundMessageReplaced);
    // This is a Hack!!! This is meant to prevent the offer not found message before
    // the finalized offer is fully loaded.
    setTimeout(() => {
      this.$.offerArticle.style.setProperty('display', 'block');
    }, 1000);
  }

  /**
   * Gets the value for the given parameter in the url
   * @param {String} name - Parameter from URL
   * @returns parameter value
   */
  _getParameterByName(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  /**
   * This method removes ns-product-small event listeners and creates
   * new ones for the updated list.
   * @param {Array} newValue - Array of products
   * @param {Array} oldValue - Array of products
   */
  async _productsChanged() {
    // Remove all event listeners
    let oldChildren = this.shadowRoot.querySelectorAll("ns-product-small");
    for (let i = 0; i < oldChildren.length; i++) {
      let card = oldChildren[i].id;
      this.shadowRoot.querySelector('#' + card).removeEventListener('qty-changed', null);
    }

    // Add event listeners
    let newChildren = this.shadowRoot.querySelectorAll("ns-product-small");
    for (let n = 0; n < newChildren.length; n++) {
      let card = newChildren[n].id;
      this.shadowRoot.querySelector('#' + card).addEventListener('qty-changed', async (e) => {
        await this._updateOfferPrice();
      });
    }
  }

  /**
   * Ported over from ns-product-group for analytics
   * @param {*} e
   */
  productSmallTap(e) {
    const tapEvent = new CustomEvent('nsProductSmallTap', {bubbles: true, composed: true, detail: { model: e.model, path: e.detail.sourceEvent.path }});
    this.dispatchEvent(tapEvent);
  }

  /**
   * This method does several things:
   * 1. Calculates total cost of products
   * 2. Calculates discounted price
   * 3. Calculates discounted difference in price
   * 4. Sets the percentage off based on discount title (description in Firebase)
   */
  async _updateOfferPrice() {
    this.productSelectionNeeded = false;
    if (this.baseSkusNotSelected.length > 0) {
      this.baseSkusNotSelected.forEach((product) => {
        let productSmall = product.id;
        let card = this.shadowRoot.querySelector('#' + productSmall);
        let cardContainer = card.shadowRoot.querySelector('.card-container');
        let cardActionWrap = cardContainer.querySelector('.card-action-wrap');
        cardContainer.removeAttribute('style');
        cardActionWrap.removeAttribute('style');
      });
    }
    let user = UserService.getUser();
    let children = this.shadowRoot.querySelectorAll("ns-product-small");
    let totalPrice = 0;
    let totalProducts = 0;
    let totalOutOfStock = 0;
    children.forEach((product) => {
      let qty = 1;
      if (product.qty !== undefined) {
        qty = parseInt(product.qty);
      }
      totalPrice += !product.notAvailable ? product.price * qty : 0;
      totalProducts += !product.notAvailable ? qty : 0;
      if (product.notAvailable || (product.__data.product.status !== "RELEASED_FOR_SALE" && product.__data.product.status !== "")) {
        totalOutOfStock++;
      }
    });

    this.addAllToBagText = this.translations.addAllItems.replace('{qty}', totalProducts);
    if (!util.MobileUtils.isMobile() && children.length < 7 || children.length < 3 && util.MobileUtils.isMobile()) {
      this.hideBottomActions = true;
    } else {
      this.hideBottomActions = false;
    }
    if (totalOutOfStock > 0) {
      this.productOutOfStock = true;
    }
    if (!Number.isNaN(totalPrice)) {
      this.bundleTotal = totalPrice;
      if ((this.discount && !user) || (user && ((user.priceType === "RTL" || user.priceType === "WRTL") || user.isGuest))) {
        let multiplier = this.discount ? this.discount.multiplier : 1
        this.discountedPrice = this.bundleTotal * multiplier;
        this.discountDifference = this.bundleTotal - this.discountedPrice;
        let discountNum = Math.round((1-multiplier)*100)
        if (parseInt(discountNum)) {
          this.percentageOff = discountNum;
          let percentTranslation = this.translations.discountPercentagePrice.replace('{percent}', discountNum);
          this.percentTranslation = percentTranslation.replace('{savings}', `<ns-currency amount="${this.discountDifference}"></ns-currency>`);
          this.offerLoading = false;
          this.$.offerArticle.style.setProperty('display', 'block');
        } else {
          this.offerLoading = false;
          this.$.offerArticle.style.setProperty('display', 'block');
        }
      } else {
        this.discountedPrice = this.bundleTotal * 1;
        this.hideDiscount = true;
        this.offerLoading = false;
        this.$.offerArticle.style.setProperty('display', 'block');
      }
      let priceString = `<span class="hide-${this.hideDiscount}"><ns-currency amount="${this.bundleTotal}"></ns-currency></span> <span><ns-currency amount="${this.discountedPrice}"></ns-currency></span>`;
      this.bundleTotalString = this.translations.bundleTotal.replace('{price}', priceString);
      this.offerNotFound = false;
    } else {
      this.offerNotFound = true;
      const userId = this._getParameterByName("userId");
      await this._getSponsorName(userId);
      this.offerLoading = false;
    }
  }
}

customElements.define(NsPersonalOfferLanding.is, NsPersonalOfferLanding);
